import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AppContext, AppContextData } from '../../contexts/AppContext';
import { CustomTopbar } from './CustomTopbar';
import { CustomSidebar } from './CustomSidebar';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { useUserHook } from '../../hooks/UserHook';
import { useIsMobile } from '../../hooks/useIsMobile';
import { footerItems, sidebarItems } from './data';
import { NavbarProps } from "../../components/layout/Sidebar";

interface PageMasterTemplateProps {
    children : JSX.Element
}

export const PageMasterTemplate = ({children} : PageMasterTemplateProps) => {

    const [selectedPipeline, setSelectedPipeline] = useState<string>("new");
    const [selectedChat, setSelectedChat] = useState<string>("new");

    const [appName, setAppName] = useState('SENGI Labs');
    const [showSidebar, setShowSidebar] = useState(true);
    const [showTopbar, setShowTopbar] = useState<boolean>(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [user, userService] = useUserHook();
    const { effectiveTheme } = useBootstrapTheme();
    const isMobile = useIsMobile();

    const contextData: AppContextData = {
        appName,
        setAppName,
        showTopbar,
        setShowTopbar,
        showSidebar,
        setShowSidebar,
        showFooter: false,
        selectedPipeline,
        setSelectedPipeline,
        selectedChat,
        setSelectedChat,
    };

    // Kein MiddleItemContainer: die Plattform-Sidebar ist der App-uebergreifende Rahmen und darf
    // keine App-Daten enthalten (z.B. Chat-Verlauf) - jede App laeuft als eigener, perspektivisch
    // separat installierbarer Container, siehe TASKS.md "Plattform-Sidebar ohne App-Daten"
    // (2026-07-16, Nutzer-Architekturentscheidung). Frueher zeigte SidebarUserHistory hier den
    // Chat-Verlauf an, war aber genau deshalb ein Architekturbruch, nicht nur unstyled.
    const navbarContent: NavbarProps = {
        items: sidebarItems,
        footerItems: footerItems,
        navbarClass: 'd-flex flex-column vh-100',
    }

    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false); // Hide on mobile
            setSidebarCollapsed(true); // Always collapsed on mobile
        } else {
            setSidebarOpen(true); // Always visible on desktop
            // Keep collapsed state as user preference on desktop
        }
    }, [isMobile]);

    return (
        <AppContext.Provider value={contextData}>
            <div className='root-grid'>
                <CustomTopbar
                    // Auf Mobile ist die Sidebar standardmaessig unsichtbar (sidebarOpen=false,
                    // siehe useEffect oben) - "collapsed" (icon-only vs. mit Text) ist dort
                    // irrelevant, wenn man die Sidebar erst gar nicht sehen kann. Der
                    // Hamburger-Button muss dort also sidebarOpen toggeln statt sidebarCollapsed,
                    // sonst gibt es auf Mobile keine Moeglichkeit, die Sidebar ueberhaupt zu
                    // oeffnen (echter Bug, 2026-07-16 live gefunden).
                    onSidebarToggle={() => isMobile ? setSidebarOpen(!sidebarOpen) : setSidebarCollapsed(!sidebarCollapsed)}
                    sidebarCollapsed={sidebarCollapsed}
                />
                <CustomSidebar 
                    isOpen={sidebarOpen}
                    isCollapsed={sidebarCollapsed}
                    navbarProps={navbarContent}
                    onClose={() => setSidebarOpen(false)}
                    onToggleText={() => setSidebarCollapsed(!sidebarCollapsed)}
                />
                <div 
                    style={{
                    marginLeft:showSidebar
                        ?
                            sidebarOpen && !isMobile
                                ? (sidebarCollapsed ? '64px' : '256px')
                                : '0'
                        : '0',
                    width: showSidebar
                        ?
                            sidebarOpen && !isMobile
                                ? (sidebarCollapsed ? 'calc(100% - 64px)' : 'calc(100% - 256px)')
                                : '100%'
                        : '100%',
                    position: 'absolute',
                    display: 'flex',
                    marginTop: showTopbar ? '64px' : '0',
                    height: showTopbar ? 'calc(100vh - 64px)' : '100vh',
                    transition: 'margin-left 0.3s ease-in-out',
                    backgroundColor: effectiveTheme === 'dark' ? '#0f172a' : '#ffffff'
                    }}
                >
                    {children || <Outlet />}
                </div>
            </div>            
        </AppContext.Provider>

    );

}
